import {DocumentStoreApi} from '@/components/document/store';
import {ModelFactory, ValueResolver, ValueFactory} from '@/engine';
import {DependencyRegistrer} from '@/engine/types/dependencies';
import {Switch} from '@/model/types';
import {createConditionFactory} from './condition';

export type SwitchProcess<T, U> = (target: T) => U;
export type SwitchTransform<U, V> = (target: U, valueResolver: ValueResolver, store?: DocumentStoreApi) => V;
export type CreateFactoryFromSwitchOptions<T, U = T, V = U> = {
  // Process is run at the model compile time
  process?: SwitchProcess<T, U>;
  // Transform is run at the model run time
  transform?: SwitchTransform<U, V>;
  dependencyRegistrer?: DependencyRegistrer;
};

export const NoOp = <T, U = T>(arg: T) => arg as unknown as U;

const sortDefaultLast = <T>(_: Switch<T>['$switch'][number], b: Switch<T>['$switch'][number]) =>
  '$default' in b ? -1 : 0;

export const createFactoryFromSwitch = <T, U = T, V = U>(
  switchObj: Switch<T>,
  defaultValue: V,
  {process = NoOp, transform = NoOp, dependencyRegistrer}: CreateFactoryFromSwitchOptions<T, U, V> = {},
): ModelFactory<V> => {
  const switchMap = switchObj.$switch.sort(sortDefaultLast).map(switchCase => ({
    $case:
      '$case' in switchCase
        ? switchCase.$case.map(condition => createConditionFactory(condition, dependencyRegistrer))
        : [],
    $then: process('$then' in switchCase ? switchCase.$then : switchCase.$default),
  }));

  return (valueResolver, visibilitySet) => {
    for (const {$case, $then} of switchMap) {
      if ($case.every(predicate => predicate(valueResolver, visibilitySet)())) {
        return transform($then, valueResolver);
      }
    }
    return defaultValue;
  };
};

export const createValueFactoryFromSwitch = <T, U, V>(
  switchObj: Switch<T>,
  defaultValue: V,
  {process = NoOp, transform = NoOp, dependencyRegistrer}: CreateFactoryFromSwitchOptions<T, U, V> = {},
): ValueFactory<V> => {
  return (store, valueResolver, visibilitySet) => {
    const factory = createFactoryFromSwitch(switchObj, defaultValue, {
      process,
      transform: value => transform(value, valueResolver, store),
      dependencyRegistrer,
    });

    return factory(valueResolver, visibilitySet);
  };
};
